import {
  AuthorizationRequest,
  AuthorizationServiceConfiguration,
  FetchRequestor,
  RedirectRequestHandler,
} from '@openid/appauth';
import { NoHashQueryStringUtils } from './NoHashQueryStringUtils';

/**
 * This function calls VPAuth to log the user in, based on the id token that is passed. When
 * authentication succeeds, VPAuth stores a cookie, and redirects the browser to the passed
 * redirectUrl. When VPC redirects the browser later to VPAuth to authenticate, VPAuth will
 * see that the user is already logged in, and returns the user to VPC.
 * This function uses the /Portal/Login endpoint, which the portal posts its data to,
 * using navigation, instead of asynchronously (using a fetch call). This is done because
 * the resulting cookies might be blocked by the browser (block third party cookies) when the
 * request would be done asynchronously (using a fetch call)
 */
export const loginWithIdToken = ({
  authorizationServerUrl,
  idToken,
  redirectUrl,
  openInNewWindow,
}) => {
  const form = document.createElement('form');
  form.action = `${authorizationServerUrl}/Portal/Login`;
  form.method = 'POST';
  if (openInNewWindow) {
    form.target = '_blank';
  }
  form.style.visibility = 'hidden';

  const formValues = { idToken, redirectUrl };
  Object.entries(formValues).forEach(([name, value]) => {
    const inputElement = document.createElement('input');
    inputElement.type = 'hidden';
    inputElement.name = name;
    inputElement.value = value;
    form.append(inputElement);
  });

  document.body.append(form);
  form.submit();
};

/**
 * When the user was just authenticated at VPAuth, this function exchanges the authorization
 * code for an identity token at the Demo Integration Portal backend.
 */
export const getIdTokenWithAuthorizationCode = async ({
  code,
  codeVerifier,
  clientId,
}) => {
  const response = await fetch('/api/vpcaccess/token', {
    method: 'POST',
    body: JSON.stringify({
      client_id: clientId,
      code,
      code_verifier: codeVerifier,
    }),
    headers: {
      'Content-Type': 'application/json',
    },
  });
  return response.json().then(x => x.identityToken);
};

/**
 * When trying to navigate to VPC, the Demo Integration Portal frontend will first ask the Demo
 * Integration Portal for an id token. If the user has successfully authenticated before through the
 * Demo Integration Portal, the backend will have a refresh token stored of the user. In this case,
 * the backend will just retrieve an id token from VPAuth based on that refresh token, without
 * requiring the user to be redirected to VPAuth and enter its credentials.
 */
export const getIdToken = async () => {
  const response = await fetch('/api/vpcaccess/getidtoken');
  switch (response.status) {
    case 200:
      return response.json().then(x => x.identityToken);
    case 401:
    case 404:
      return null;
    default:
      throw new Error(`Status code ${response.status} werd niet verwacht`);
  }
};

/**
 * This function will start OAuth2's authorization code flow with PKCE to let the user authenticate
 * at VPAuth.
 */
export const startVPCAuthentication = async ({ authorizationServerUrl, clientId }) => {
  const serviceConfig = await AuthorizationServiceConfiguration.fetchFromIssuer(
    authorizationServerUrl,
    new FetchRequestor(),
  );

  const authorizationRequest = new AuthorizationRequest({
    client_id: clientId,
    redirect_uri: `${window.location.origin}/vpc-authorization-callback`,
    scope: 'openid offline_access', // pass offline_access to request a refresh token
    response_type: AuthorizationRequest.RESPONSE_TYPE_CODE,
    extras: { prompt: 'login' },
  });

  const authorizationHandler = new RedirectRequestHandler(undefined, new NoHashQueryStringUtils());
  authorizationHandler.performAuthorizationRequest(serviceConfig, authorizationRequest);
};
